// Combined categorizations (breakdownSource "ruleSets"): a Count metric that evaluates several
// categorization rule sets INDEPENDENTLY over the same rows and shows chosen categories of each
// as rows of one table - each as a count, or as a share of its own rule set.
//
// Semantics - mirrored by ReportSqlBuilder.CombinedRuleSets.cs and ReportPivotBuilder.PivotCombined:
//   - Each rule set is applied on its own. Its include/exclude rules only decide whether THAT
//     rule set counts a row; they never remove the row from another rule set. So one call can
//     be "Within 30s" in one rule set and "Within 60s" in another.
//   - Each rule set's category is its usual CASE (logic/detailRuleSets.ts ruleSetCaseExpr):
//     Set rules in order, then the else label / the metric's breakdown value / nothing.
//   - A "share" row is the category divided by all categories of the same rule set in the
//     same period. Its Total is recalculated from the totals, never averaged across periods.
//
// Everything here works on configuration only (no query) - it powers the editor, the checks
// and the "Table structure" preview. Circular with ./ruleInterpretation (isMetricReady uses
// combinedRuleSetIssues); safe because both sides only call each other inside functions.
import type { ReportLogicContext } from "./context";
import type { BreakdownSource, CombinedRow, CombinedRowValue, DataColumn, DisplayAs, ExtraBreakdown, MetricSection, ModelKey, ReportConfig, RuleNode, RuleSet } from "../types";
import { isRuleSetReady, ruleHasResult, ruleLogicOutline, ruleResultValue, ruleSetModelKey, ruleValueFromColumn } from "./ruleInterpretation";
import { allSelectableColumnsForModel, breakdownColumnForModel, findModelColumn, findReportColumn, isTimestampColumnForModel, resolveCountDistinctColumn } from "./catalogResolution";
import { basedOnColumnFor, metricOverridesReportPeriod } from "./periodBounds";
import { ruleLeafConditions } from "./rules";
import {
    COMBINED_MIN_RULE_SETS,
    COMBINED_ROW_LABEL_MAX_LENGTH,
    combinedRowLabel,
    combinedRuleSetIds,
    combinedRows,
    generatedCombinedRowLabel,
    usesCombinedRuleSets,
} from "./combinedRuleSetSelection";

export interface RuleSetCategories {
    /** Fixed categories the rule set can produce, in rule order (else label last). */
    categories: string[];
    /** A Set rule writes another column's value, so some categories are only known at run time. */
    valuesFromColumns: boolean;
    /** Unmatched rows fall back to a column's value (no else label), also only known at run time. */
    elseFromColumn: boolean;
}

/** The categories a rule set produces, as far as configuration can tell. */
export function ruleSetCategories(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet): RuleSetCategories {
    const categories: string[] = [];
    const seen = new Set<string>();
    const push = (value: string) => {
        const text = value.trim();
        if (!text || seen.has(text.toLowerCase())) return;
        seen.add(text.toLowerCase());
        categories.push(text);
    };
    let valuesFromColumns = false;
    const modelKey = ruleSetModelKey(context, report, ruleSet);
    ruleSet.rules.forEach((rule) => {
        if (rule.action !== "set") return;
        if (ruleValueFromColumn(rule)) {
            if (ruleHasResult(context, report, rule, modelKey)) valuesFromColumns = true;
            return;
        }
        push(ruleResultValue(rule));
    });
    let elseFromColumn = false;
    if (ruleSet.includeElse !== false) {
        if (ruleSet.elseLabel.trim()) push(ruleSet.elseLabel);
        else elseFromColumn = true;
    }
    return { categories, valuesFromColumns, elseFromColumn };
}

/** True when `category` is one the rule set is known to produce (case-insensitive, like SQL Server). */
export function ruleSetProducesCategory(info: RuleSetCategories, category: string) {
    const key = (category || "").trim().toLowerCase();
    return info.categories.some((item) => item.toLowerCase() === key);
}

/** Condition columns that are not on the metric's data model. Such conditions would be dropped and widen the rule. */
export function ruleSetForeignColumns(context: ReportLogicContext, report: ReportConfig, section: MetricSection, ruleSet: RuleSet): string[] {
    const modelScope: ReportConfig = { ...report, models: section.model ? [section.model] : [] };
    const foreign = new Set<string>();
    ruleSet.rules.forEach((rule) => ruleLeafConditions(rule.conditions).forEach((condition) => {
        [condition.column, condition.compareColumn].forEach((columnKey) => {
            if (columnKey && !findReportColumn(context, modelScope, columnKey)) foreign.add(columnKey);
        });
    }));
    return Array.from(foreign);
}

/**
 * Why a rule set can't be combined into this metric, in words for the "Add rule set" menu and
 * the checks; null when it can. "Already added" is left to the caller.
 */
export function combinedRuleSetUnavailableReason(context: ReportLogicContext, report: ReportConfig, section: MetricSection, ruleSet: RuleSet): string | null {
    if (!ruleSet.rules.some((rule) => rule.action === "set")) return "Has no Set rule, so it creates no categories (it only filters rows)";
    if (!isRuleSetReady(context, report, ruleSet)) return "Not ready: finish its rules on the Categorization tab";
    const info = ruleSetCategories(context, report, ruleSet);
    if (!info.categories.length && !info.valuesFromColumns) return "Has no complete Set rule yet";
    // A rule set is evaluated on the data model its columns belong to. One on another model of
    // the report is combined through its own query (see "Combining rule sets from several data
    // models" below); one whose columns are spread over several models can't be evaluated at all.
    const modelKey = combinedRuleSetModelKey(context, report, section, ruleSet);
    if (!modelKey) {
        const foreign = ruleSetForeignColumns(context, report, section, ruleSet);
        return `Uses columns that are not all on one data model of this report (${foreign.join(", ")})`;
    }
    // A rule set on another model of the report is always combinable: each data model applies the
    // metric's settings where it has the columns (combinedSectionForModel and friends).
    return null;
}

// --- Combining rule sets from several data models ---------------------------------------
//
// Mirrors ReportSqlBuilder.CombinedRuleSets.cs. Rule sets are not stored with a data model:
// a rule set is on the metric's model when every column it uses is there (every config saved
// before cross-model combining existed), else on the first other model of the report that has
// all of them. Each model then gets its own SELECT - its own table, reporting period (Based On
// date), client scope and exclusions - and the SELECTs are joined with UNION ALL. Settings that
// are built from the metric's own model; each other model applies them where it has the columns
// and falls back otherwise, with a note in the SQL: Period Date / own period filter -> its Based On
// date, a filter -> skipped, a breakdown -> blank, a calculated-field measure -> counting rows,
// distinct counting -> the same column, else its record key (combinedSectionForModel and friends).

/** Column identities a rule set uses. Same scan as ReportSqlBuilder.RuleSetMissingColumns. */
function ruleSetColumnKeys(ruleSet: RuleSet): string[] {
    const keys: string[] = [];
    ruleSet.rules.forEach((rule) => {
        ruleLeafConditions(rule.conditions).forEach((condition) => {
            if (condition.column) keys.push(condition.column);
            if ((condition.compareTo === "column" || condition.leftKind === "minutes_between") && condition.compareColumn) keys.push(condition.compareColumn);
        });
        const target = (rule.targetField || "").trim();
        if (target && target !== "category") keys.push(target);
        if (ruleValueFromColumn(rule) && rule.valueColumn) keys.push(rule.valueColumn);
    });
    return keys;
}

function columnsMissingOnModel(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey, keys: string[]) {
    const modelScope: ReportConfig = { ...report, models: [modelKey] };
    return Array.from(new Set(keys.filter((key) => !findReportColumn(context, modelScope, key))));
}

/** The data model a combined rule set is evaluated on, or "" when no single model of the report has all its columns. */
export function combinedRuleSetModelKey(context: ReportLogicContext, report: ReportConfig, section: MetricSection, ruleSet: RuleSet): ModelKey | "" {
    if (!section.model) return "";
    const keys = ruleSetColumnKeys(ruleSet);
    if (!columnsMissingOnModel(context, report, section.model, keys).length) return section.model;
    return report.models.find((modelKey) => modelKey !== section.model && !columnsMissingOnModel(context, report, modelKey, keys).length) || "";
}

/** The other data models the metric's combined rule sets are on (empty for a single-model metric). */
export function combinedOtherModelKeys(context: ReportLogicContext, report: ReportConfig, section: MetricSection): ModelKey[] {
    const keys = new Set<ModelKey>();
    combinedRuleSetIds(section).forEach((id) => {
        const ruleSet = report.bucketRuleSets.find((set) => set.id === id);
        const modelKey = ruleSet ? combinedRuleSetModelKey(context, report, section, ruleSet) : "";
        if (modelKey && modelKey !== section.model) keys.add(modelKey);
    });
    return Array.from(keys);
}

/** Label of the rule set's data model when it is not the metric's own ("" otherwise). */
export function combinedRuleSetOtherModelLabel(context: ReportLogicContext, report: ReportConfig, section: MetricSection, ruleSet: RuleSet) {
    const modelKey = combinedRuleSetModelKey(context, report, section, ruleSet);
    return modelKey && modelKey !== section.model ? context.dataModels[modelKey]?.label || modelKey : "";
}

/** Metric filter column identities (condition trees, or the legacy single column). */
function metricFilterColumnKeys(section: MetricSection): string[] {
    const fromNodes = (nodes?: RuleNode[]) => ruleLeafConditions(nodes || []).flatMap((condition) => {
        const keys = condition.column ? [condition.column] : [];
        if ((condition.compareTo === "column" || condition.leftKind === "minutes_between") && condition.compareColumn) keys.push(condition.compareColumn);
        return keys;
    });
    const keys = fromNodes(section.filterConditions).concat(fromNodes(section.excludeFilterConditions));
    if (!section.filterConditions?.length && section.filterValues?.length && section.filterColumn) keys.push(section.filterColumn);
    if (!section.excludeFilterConditions?.length && section.excludeFilterValues?.length && section.excludeFilterColumn) keys.push(section.excludeFilterColumn);
    return keys;
}

/**
 * The column an extra breakdown uses on another data model: the same column when that model
 * has it, else - for a date part - that model's Based On date. Null when there is none.
 * Mirrors ReportSqlBuilder.CombinedAxisColumnFor.
 */
export function combinedAxisColumnFor(context: ReportLogicContext, report: ReportConfig, extra: ExtraBreakdown, metricColumn: DataColumn, modelKey: ModelKey): DataColumn | null {
    const same = breakdownColumnForModel(context, modelKey, metricColumn.key);
    if (same && (!extra.granularity || isTimestampColumnForModel(context, modelKey, same.key))) return same;
    return extra.granularity ? basedOnColumnFor(context, report, modelKey) : null;
}

/**
 * The metric as one data model of a combined query sees it. Mirrors CombinedSectionForModel:
 * the metric's own model gets it unchanged; another model keeps the Period Date only when it
 * has every column the Period Date uses, and the metric's own period filter only when it has
 * the filter's condition columns - otherwise it uses the report's period on its Based On date.
 * The returned section is on `modelKey`; `notes` say what was replaced.
 */
export function combinedSectionForModel(context: ReportLogicContext, report: ReportConfig, section: MetricSection, modelKey: ModelKey): { section: MetricSection; notes: string[] } {
    if (modelKey === section.model) return { section, notes: [] };
    const label = context.dataModels[modelKey]?.label || modelKey;
    const conditionKeys = (nodes?: RuleNode[]) => ruleLeafConditions(nodes || []).flatMap((condition) => {
        const keys = condition.column ? [condition.column] : [];
        if ((condition.compareTo === "column" || condition.leftKind === "minutes_between") && condition.compareColumn) keys.push(condition.compareColumn);
        return keys;
    });
    const dateOn = (key?: string) => !!key && isTimestampColumnForModel(context, modelKey, key);
    const periodDate = section.periodDate;
    const keepPeriodDate = !!periodDate && dateOn(periodDate.elseDateColumn)
        && periodDate.cases.every((item) => dateOn(item.dateColumn) && !columnsMissingOnModel(context, report, modelKey, conditionKeys(item.conditions)).length);
    const overrides = metricOverridesReportPeriod(section);
    const keepPeriodFilter = overrides && !columnsMissingOnModel(context, report, modelKey, conditionKeys(section.periodFilter?.conditions)).length;
    // One side of the metric's filters, read the way buildMetricFilterClauses reads it.
    const sideUsable = (nodes: RuleNode[] | undefined, column: string, values: string[]) => (nodes?.length
        ? !columnsMissingOnModel(context, report, modelKey, conditionKeys(nodes)).length
        : !column || !values?.length || !columnsMissingOnModel(context, report, modelKey, [column]).length);
    const dropInclude = !sideUsable(section.filterConditions, section.filterColumn, section.filterValues);
    const dropExclude = !sideUsable(section.excludeFilterConditions, section.excludeFilterColumn, section.excludeFilterValues);
    const notes: string[] = [];
    if (periodDate && !keepPeriodDate) notes.push(`${label} lacks a column the Period Date uses: dated by its Based On date instead.`);
    if (overrides && !keepPeriodFilter) notes.push(`${label} lacks a column the metric's period filter uses: the report's reporting period applies instead.`);
    if (dropInclude) notes.push(`${label} lacks a column the metric's "only rows where" filter uses: not applied to its rows.`);
    if (dropExclude) notes.push(`${label} lacks a column the metric's "leave out rows where" filter uses: not applied to its rows.`);
    return {
        section: {
            ...section,
            model: modelKey,
            periodDate: keepPeriodDate ? periodDate : undefined,
            periodFilter: overrides && !keepPeriodFilter ? undefined : section.periodFilter,
            ...(dropInclude ? { filterConditions: undefined, filterColumn: "", filterValues: [] } : {}),
            ...(dropExclude ? { excludeFilterConditions: undefined, excludeFilterColumn: "", excludeFilterValues: [] } : {}),
        },
        notes,
    };
}

/**
 * Distinct counting on one data model of a combined metric. Mirrors CombinedDistinctColumnFor:
 * each SELECT counts its own rows and every rule set lives in one SELECT, so distinct counts are
 * never added across models. The metric's model uses the chosen column; another model uses the
 * column with the same identity, else its record key, else counts rows. `note` explains which.
 */
export function combinedDistinctColumnFor(context: ReportLogicContext, section: MetricSection, modelKey: ModelKey): { column: DataColumn | null; note: string | null } {
    if (section.countMode !== "distinct" || !section.countDistinctColumn) return { column: null, note: null };
    if (modelKey === section.model) return { column: resolveCountDistinctColumn(context, modelKey, section.countMode, section.countDistinctColumn), note: null };
    const label = context.dataModels[modelKey]?.label || modelKey;
    const same = findModelColumn(context, modelKey, section.countDistinctColumn);
    if (same) return { column: same, note: `Distinct ${same.label} on ${label}` };
    const recordKeyName = context.dataModels[modelKey]?.recordKeyColumn;
    const recordKey = recordKeyName ? findModelColumn(context, modelKey, recordKeyName) : null;
    return recordKey
        ? { column: recordKey, note: `${label} has no ${section.countDistinctColumn}: counting distinct ${recordKey.label} (its record key)` }
        : { column: null, note: `${label} has no ${section.countDistinctColumn} and no record key: counting rows` };
}

/** Explicit row overrides use the row's own source model; inherited settings keep legacy fallbacks. */
export function combinedRowCountColumns(context: ReportLogicContext, report: ReportConfig, section: MetricSection, row: CombinedRow) {
    const ruleSet = report.bucketRuleSets.find((set) => set.id === row.ruleSetId);
    return allSelectableColumnsForModel(context, ruleSet ? combinedRuleSetModelKey(context, report, section, ruleSet) : "");
}


/** "answered calls"-style scope text from the rule set's Include/Exclude rules ("" when it has none). */
export function combinedRuleSetScope(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet) {
    const includes = ruleSet.rules.filter((rule) => rule.action === "include").map((rule) => ruleLogicOutline(context, report, rule)).filter(Boolean);
    const excludes = ruleSet.rules.filter((rule) => rule.action === "exclude").map((rule) => ruleLogicOutline(context, report, rule)).filter(Boolean);
    const parts: string[] = [];
    if (includes.length) parts.push(`only rows where ${includes.join(" or ")}`);
    if (excludes.length) parts.push(`leaving out ${excludes.join(" or ")}`);
    return parts.join("; ");
}

/** One line under the rule set's name in the editor. */
export function combinedRuleSetSummary(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet) {
    const info = ruleSetCategories(context, report, ruleSet);
    const count = info.categories.length;
    const categoryText = count
        ? `${count} categor${count === 1 ? "y" : "ies"} (${info.categories.join(", ")})`
        : "Categories come from column values";
    const extra = info.valuesFromColumns && count ? " plus column values" : "";
    const scope = combinedRuleSetScope(context, report, ruleSet);
    return `${categoryText}${extra}${scope ? ` · ${scope}` : ""}`;
}

/** What a share row is divided by, in words. */
export function combinedShareExplanation(context: ReportLogicContext, report: ReportConfig, row: CombinedRow) {
    const ruleSet = report.bucketRuleSets.find((set) => set.id === row.ruleSetId);
    const name = ruleSet?.name || "its rule set";
    const scope = ruleSet ? combinedRuleSetScope(context, report, ruleSet) : "";
    return `${row.category || "This category"} ÷ every category of ${name}${scope ? ` (${scope})` : ""} in the same period. The Total is recalculated from the totals.`;
}

// --- editing ------------------------------------------------------------------------

export function makeCombinedRow(context: ReportLogicContext, ruleSetId: string, category: string, value: CombinedRowValue): CombinedRow {
    return { id: context.nextId("crow"), ruleSetId, category, value, label: generatedCombinedRowLabel(category, value), labelCustom: false };
}

/** One count row per known category of the rule set - what "Add all categories" and adding a rule set create. */
export function seedCombinedRows(context: ReportLogicContext, report: ReportConfig, ruleSetId: string): CombinedRow[] {
    const ruleSet = report.bucketRuleSets.find((set) => set.id === ruleSetId);
    if (!ruleSet) return [];
    return ruleSetCategories(context, report, ruleSet).categories.map((category) => makeCombinedRow(context, ruleSetId, category, "count"));
}

/** The usual SLA pair for one category: its share, then its count. */
export function countAndShareRows(context: ReportLogicContext, ruleSetId: string, category: string): CombinedRow[] {
    return [makeCombinedRow(context, ruleSetId, category, "share"), makeCombinedRow(context, ruleSetId, category, "count")];
}

/** Applies a change to a row and keeps a generated label in step with its category/value. */
export function withCombinedRowChange(row: CombinedRow, change: Partial<Pick<CombinedRow, "ruleSetId" | "category" | "value">>): CombinedRow {
    const next = { ...row, ...change };
    return next.labelCustom ? next : { ...next, label: generatedCombinedRowLabel(next.category, next.value) };
}

/** Typing a label makes it custom; clearing it hands the label back to the generator. */
export function withCombinedRowLabel(row: CombinedRow, text: string): CombinedRow {
    return text.trim()
        ? { ...row, label: text, labelCustom: true }
        : { ...row, label: generatedCombinedRowLabel(row.category, row.value), labelCustom: false };
}

/** A copy right after the row, flipped between count and share - the quickest way to build a pair. */
export function duplicateCombinedRow(context: ReportLogicContext, section: MetricSection, rowId: string): MetricSection {
    const rows = combinedRows(section);
    const index = rows.findIndex((row) => row.id === rowId);
    if (index < 0) return section;
    const source = rows[index];
    const copy = makeCombinedRow(context, source.ruleSetId, source.category, source.value === "share" ? "count" : "share");
    copy.countMode = source.countMode;
    copy.countDistinctColumn = source.countDistinctColumn;
    const next = rows.slice();
    next.splice(index + 1, 0, copy);
    return { ...section, combinedRows: next };
}

export function moveInList<T>(items: T[], index: number, direction: -1 | 1): T[] {
    const target = index + direction;
    if (index < 0 || target < 0 || target >= items.length) return items;
    const next = items.slice();
    [next[index], next[target]] = [next[target], next[index]];
    return next;
}

/** Adds a rule set and its categories as count rows. */
export function addCombinedRuleSet(context: ReportLogicContext, report: ReportConfig, section: MetricSection, ruleSetId: string): { section: MetricSection; addedRows: number } {
    const ids = combinedRuleSetIds(section);
    if (!ruleSetId || ids.includes(ruleSetId)) return { section, addedRows: 0 };
    const rows = seedCombinedRows(context, report, ruleSetId);
    return {
        section: { ...section, combinedRuleSetIds: [...ids, ruleSetId], combinedRows: [...combinedRows(section), ...rows] },
        addedRows: rows.length,
    };
}

/** Removes a rule set, the rows built on it, and their Excel options. */
export function removeCombinedRuleSet(section: MetricSection, ruleSetId: string): MetricSection {
    const removedRowIds = new Set(combinedRows(section).filter((row) => row.ruleSetId === ruleSetId).map((row) => row.id));
    const excelLayout = section.excelLayout && (section.excelLayout.combinedRowOptions || section.excelLayout.combinedRowOrder)
        ? {
            ...section.excelLayout,
            combinedRowOptions: Object.fromEntries(Object.entries(section.excelLayout.combinedRowOptions || {}).filter(([id]) => !removedRowIds.has(id))),
            combinedRowOrder: (section.excelLayout.combinedRowOrder || []).filter((id) => !removedRowIds.has(id)),
        }
        : section.excelLayout;
    return {
        ...section,
        combinedRuleSetIds: combinedRuleSetIds(section).filter((id) => id !== ruleSetId),
        combinedRows: combinedRows(section).filter((row) => !removedRowIds.has(row.id)),
        excelLayout,
    };
}

/**
 * Changes "Break Down Using". Nothing is thrown away: leaving the combined mode keeps its rule
 * sets and rows as a draft on the metric (ignored by both SQL builders while the mode is off),
 * so switching back restores them. Returns a note for the editor to show, or "".
 */
export function switchBreakdownSource(context: ReportLogicContext, report: ReportConfig, section: MetricSection, next: BreakdownSource): { section: MetricSection; note: string } {
    if (next === section.breakdownSource) return { section, note: "" };
    const nameOf = (id: string) => report.bucketRuleSets.find((set) => set.id === id)?.name || "Untitled Rule Set";
    if (next === "ruleSets") {
        const draftIds = combinedRuleSetIds(section).filter((id) => report.bucketRuleSets.some((set) => set.id === id));
        const draftRows = combinedRows(section).filter((row) => draftIds.includes(row.ruleSetId));
        if (draftIds.length) {
            return {
                section: { ...section, breakdownSource: "ruleSets", combinedRuleSetIds: draftIds, combinedRows: draftRows },
                note: `Restored your combined setup (${draftIds.length} rule set${draftIds.length === 1 ? "" : "s"}, ${draftRows.length} row${draftRows.length === 1 ? "" : "s"}).`,
            };
        }
        const start = section.ruleSetId && report.bucketRuleSets.some((set) => set.id === section.ruleSetId) ? section.ruleSetId : "";
        return {
            section: { ...section, breakdownSource: "ruleSets", combinedRuleSetIds: start ? [start] : [], combinedRows: start ? seedCombinedRows(context, report, start) : [] },
            note: start ? `Started from "${nameOf(start)}". Add at least one more rule set below.` : "",
        };
    }
    if (usesCombinedRuleSets(section)) {
        const ids = combinedRuleSetIds(section);
        const keepSingle = next === "ruleSet" ? (section.ruleSetId && ids.includes(section.ruleSetId) ? section.ruleSetId : ids[0] || section.ruleSetId) : section.ruleSetId;
        const rows = combinedRows(section).length;
        return {
            section: { ...section, breakdownSource: next, ruleSetId: keepSingle || "" },
            note: ids.length
                ? `Your combined setup (${ids.length} rule set${ids.length === 1 ? "" : "s"}, ${rows} row${rows === 1 ? "" : "s"}) is kept. Choose "Several Rule Sets - Combined Table" again to restore it.`
                : "",
        };
    }
    return { section: { ...section, breakdownSource: next }, note: "" };
}

/** Show As changes: combined tables are Count-only, so another display falls back to a single rule set. */
export function switchDisplayAs(context: ReportLogicContext, report: ReportConfig, section: MetricSection, displayAs: DisplayAs): { section: MetricSection; note: string } {
    if (!usesCombinedRuleSets(section) || displayAs === "count") return { section: { ...section, displayAs }, note: "" };
    const switched = switchBreakdownSource(context, report, section, "ruleSet");
    const kept = report.bucketRuleSets.find((set) => set.id === switched.section.ruleSetId)?.name;
    return {
        section: { ...switched.section, displayAs },
        note: `Combined tables are available for Count only, so this metric now uses one rule set${kept ? ` ("${kept}")` : ""}. ${switched.note}`.trim(),
    };
}

// --- checks -------------------------------------------------------------------------

export interface CombinedIssue {
    key: string;
    severity: "error" | "warning";
    message: string;
    ruleSetId?: string;
    rowId?: string;
}

/** Everything that would stop the server building the query, plus a few warnings. Mirrors ReportSqlBuilder's checks. */
export function combinedRuleSetIssues(context: ReportLogicContext, report: ReportConfig, section: MetricSection): CombinedIssue[] {
    if (!usesCombinedRuleSets(section)) return [];
    const issues: CombinedIssue[] = [];
    const ids = combinedRuleSetIds(section);

    if (section.displayAs !== "count") {
        issues.push({ key: "displayAs", severity: "error", message: "Combined tables are available for Count metrics only." });
    }
    if (section.measure?.fieldId && section.measure.aggregate !== "sum") {
        issues.push({ key: "measure", severity: "error", message: "A combined table can count rows or add up a calculated field (\"Sum of\"), not average it." });
    }
    if (ids.length < COMBINED_MIN_RULE_SETS) {
        issues.push({ key: "ruleSets.min", severity: "error", message: "Add at least two rule sets, or switch Break Down Using to Categorization Rule Set." });
    }

    const seen = new Set<string>();
    const categoriesById = new Map<string, RuleSetCategories>();
    ids.forEach((id) => {
        if (seen.has(id)) {
            issues.push({ key: `ruleSet.${id}.duplicate`, severity: "error", message: "The same rule set is combined more than once.", ruleSetId: id });
            return;
        }
        seen.add(id);
        const ruleSet = report.bucketRuleSets.find((set) => set.id === id);
        if (!ruleSet) {
            issues.push({ key: `ruleSet.${id}.missing`, severity: "error", message: "A combined rule set no longer exists. Remove it from this metric.", ruleSetId: id });
            return;
        }
        const reason = combinedRuleSetUnavailableReason(context, report, section, ruleSet);
        if (reason) issues.push({ key: `ruleSet.${id}.unavailable`, severity: "error", message: `Rule set "${ruleSet.name || "Untitled Rule Set"}": ${reason}.`, ruleSetId: id });
        categoriesById.set(id, ruleSetCategories(context, report, ruleSet));
    });

    const rows = combinedRows(section);
    if (!rows.length) {
        issues.push({ key: "rows.required", severity: "error", message: "Add at least one row to show." });
    }

    const labelCounts = new Map<string, number>();
    rows.forEach((row) => {
        const key = combinedRowLabel(row).toLowerCase();
        labelCounts.set(key, (labelCounts.get(key) || 0) + 1);
    });

    rows.forEach((row) => {
        const label = combinedRowLabel(row);
        if (!section.measure?.fieldId && row.countMode != null) {
            const ruleSet = report.bucketRuleSets.find((set) => set.id === row.ruleSetId);
            const model = ruleSet ? combinedRuleSetModelKey(context, report, section, ruleSet) : "";
            if (row.countMode !== "all" && row.countMode !== "distinct") {
                issues.push({ key: `row.${row.id}.countMode`, severity: "error", message: `Row "${label}": choose a valid count mode.`, rowId: row.id });
            } else if (row.countMode === "distinct" && !resolveCountDistinctColumn(context, model, "distinct", row.countDistinctColumn || "")) {
                issues.push({ key: `row.${row.id}.distinct`, severity: "error", message: `Row "${label}": choose an available distinct column.`, rowId: row.id });
            }
        }
        if (!ids.includes(row.ruleSetId)) {
            issues.push({ key: `row.${row.id}.ruleSet`, severity: "error", message: `Row "${label}" uses a rule set that is not combined in this metric. Pick another category or remove the row.`, rowId: row.id });
            return;
        }
        const info = categoriesById.get(row.ruleSetId);
        if (info && !ruleSetProducesCategory(info, row.category) && !info.valuesFromColumns && !info.elseFromColumn) {
            const ruleSetName = report.bucketRuleSets.find((set) => set.id === row.ruleSetId)?.name || "its rule set";
            issues.push({ key: `row.${row.id}.category`, severity: "error", message: `Row "${label}": "${row.category}" is no longer produced by ${ruleSetName}.`, rowId: row.id, ruleSetId: row.ruleSetId });
        }
        if ((labelCounts.get(label.toLowerCase()) || 0) > 1) {
            issues.push({ key: `row.${row.id}.label.duplicate`, severity: "error", message: `Two rows are named "${label}". Row labels must be unique.`, rowId: row.id });
        }
        if (label.length > COMBINED_ROW_LABEL_MAX_LENGTH) {
            issues.push({ key: `row.${row.id}.label.length`, severity: "error", message: `Row label "${label.slice(0, 40)}..." must be ${COMBINED_ROW_LABEL_MAX_LENGTH} characters or fewer.`, rowId: row.id });
        }
        const ruleSet = report.bucketRuleSets.find((set) => set.id === row.ruleSetId);
        if (row.value === "share" && info && ruleSet?.includeElse === false && info.categories.length === 1 && !info.valuesFromColumns) {
            issues.push({ key: `row.${row.id}.share.always100`, severity: "warning", message: `Row "${label}" would always be 100%: its rule set hides unmatched rows and has one category. Turn on "include else" for the rule set or show a count.`, rowId: row.id });
        }
    });
    return issues;
}

/** True when nothing blocks the query. */
export function isCombinedRuleSetsReady(context: ReportLogicContext, report: ReportConfig, section: MetricSection) {
    return !combinedRuleSetIssues(context, report, section).some((issue) => issue.severity === "error");
}

/**
 * Removes "-- ..." comments outside string literals and bracketed names, joining the lines.
 * Mirrors ReportSqlBuilder.StripSqlLineComments; used for the excluded-records preview.
 */
export function stripSqlLineComments(sql: string) {
    let out = "";
    let inString = false;
    let inBracket = false;
    for (let i = 0; i < sql.length; i++) {
        const c = sql[i];
        if (inString) {
            out += c;
            if (c === "'") {
                if (sql[i + 1] === "'") { out += "'"; i++; } else inString = false;
            }
            continue;
        }
        if (inBracket) {
            out += c;
            if (c === "]") {
                if (sql[i + 1] === "]") { out += "]"; i++; } else inBracket = false;
            }
            continue;
        }
        if (c === "-" && sql[i + 1] === "-") {
            while (i < sql.length && sql[i] !== "\n") i++;
            out += " ";
            continue;
        }
        if (c === "'") inString = true;
        else if (c === "[") inBracket = true;
        out += c === "\n" || c === "\r" ? " " : c;
    }
    return out;
}
